/**
 * Schichttyp — Blatt zum Anlegen und Ändern.
 *
 * Umbau auf die Leitstand-Optik (18.08.2026): gleiche Felder, gleiche
 * Schritte — nur ohne feste Farbwerte. Die Bestätigung zum Deaktivieren
 * liegt jetzt als zweistufiger Knopf im Blatt statt in einem Fenster
 * darüber; das erspart den Sprung aus der Seite heraus.
 *
 * Die Auswahlfarben der Schicht sind Nutzdaten (sie landen in der Datenbank
 * und färben den Schichtplan) — deshalb stehen sie hier als feste Werte und
 * nicht als Token.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../App';
import { LeitstandRahmen } from '../../components/leitstand/LeitstandRahmen';
import { Karte, Notiz, Zustand } from '../../components/leitstand';

const EINGABE =
  'rounded-[4px] border border-linie-2 bg-flaeche-2 px-3 py-2 text-sm text-tinte focus:border-akzent focus:outline-none disabled:opacity-60';
const KNOPF = 'rounded-[4px] px-3 py-1.5 font-zahl text-xs disabled:opacity-50';
const KNOPF_PRIMAER = `${KNOPF} border border-akzent bg-akzent text-akzent-satz`;
const KNOPF_LEISE = `${KNOPF} border border-linie-2 bg-flaeche text-tinte-2`;
const MARKE = 'font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3';

/** Vorschläge für die Färbung im Schichtplan — Nutzdaten, keine Token. */
const FARBVORSCHLAEGE = ['#0D6068', '#2E7D5B', '#C9922E', '#B4442E', '#5B7596', '#7A6BA8'];

interface ShiftType {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  color?: string | null;
}

interface FormData {
  name: string;
  color: string; // '' = keine Farbe (Standard-Optik)
}

const INITIAL_FORM: FormData = {
  name: '',
  color: '',
};

// Generate internal code from name (not shown in UI)
const generateCodeFromName = (name: string, existingCodes: string[]): string => {
  let base = name
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Diakritika entfernen
    .replace(/[^A-Z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  if (!base) base = 'TYPE';

  if (!existingCodes.includes(base)) return base;

  let suffix = 2;
  while (existingCodes.includes(`${base}_${suffix}`)) {
    suffix++;
  }
  return `${base}_${suffix}`;
};

export default function ShiftTypeDetailPage() {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [shiftType, setShiftType] = useState<ShiftType | null>(null);
  const [allTypes, setAllTypes] = useState<ShiftType[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [inactiveConflict, setInactiveConflict] = useState<{ id: string; name: string } | null>(
    null,
  );

  const loadAllTypes = async () => {
    try {
      const res = await fetch('/api/admin/shift-types', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAllTypes(data);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Schichttypen:', err);
    }
  };

  const loadShiftType = async () => {
    if (isNew || !id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/shift-types/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setShiftType(data);
        setFormData({
          name: data.name,
          color: data.color ?? '',
        });
      } else if (res.status === 404) {
        navigate('/einrichten/schichten?focus=types');
      }
    } catch (err) {
      console.error('Fehler beim Laden des Schichttyps:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAllTypes();
    if (!isNew) {
      loadShiftType();
    }
  }, [id, isNew]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Name ist erforderlich');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const url = isNew ? '/api/admin/shift-types' : `/api/admin/shift-types/${id}`;
      let body: { code?: string; name: string; color?: string | null };

      if (isNew) {
        const existingCodes = allTypes.map((t) => t.code);
        body = {
          code: generateCodeFromName(formData.name.trim(), existingCodes),
          name: formData.name.trim(),
          color: formData.color || null,
        };
      } else {
        body = { name: formData.name.trim(), color: formData.color || null };
      }

      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (res.ok) {
        const data = await res.json();
        const savedId = isNew ? data.id : id;
        // Direkt zur Schichten-Übersicht mit Focus auf Typen-Sektion
        navigate(`/einrichten/schichten?focus=types&id=${savedId}`, { replace: true });
      } else {
        const data = await res.json().catch(() => ({}));
        // Spezielle Behandlung für Duplikate
        if (data.code === 'SHIFT_TYPE_EXISTS_ACTIVE') {
          setError('Ein Schichttyp mit diesem Namen existiert bereits');
          setInactiveConflict(null);
        } else if (data.code === 'SHIFT_TYPE_EXISTS_INACTIVE' && data.existingId) {
          setError(null);
          setInactiveConflict({ id: data.existingId, name: data.existingName || formData.name });
        } else {
          setError(data.error || data.message || 'Fehler beim Speichern');
          setInactiveConflict(null);
        }
      }
    } catch (err) {
      setError('Netzwerkfehler');
    }
    setSaving(false);
  };

  const handleToggleActive = async () => {
    if (!id || isNew || !shiftType) return;

    setDeleting(true);
    try {
      const newIsActive = !shiftType.isActive;
      const res = await fetch(`/api/admin/shift-types/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isActive: newIsActive }),
      });

      if (res.ok) {
        // UI aktualisieren
        setShiftType({ ...shiftType, isActive: newIsActive });
        setDeleteConfirmOpen(false);
      }
    } catch (err) {
      console.error('Fehler beim Statuswechsel:', err);
    }
    setDeleting(false);
  };

  if (loading) {
    return (
      <LeitstandRahmen titel="Schichttyp" benutzer={user?.displayName}>
        <p className="py-12 text-center text-sm text-tinte-3">Lade Schichttyp …</p>
      </LeitstandRahmen>
    );
  }

  if (!isNew && !shiftType) {
    return (
      <LeitstandRahmen titel="Schichttyp" benutzer={user?.displayName}>
        <p className="py-12 text-center text-sm text-tinte-3">Schichttyp nicht gefunden</p>
      </LeitstandRahmen>
    );
  }

  return (
    <LeitstandRahmen
      titel={isNew ? 'Neuer Schichttyp' : shiftType?.name || 'Schichttyp'}
      benutzer={user?.displayName}
      werkzeuge={
        <span className="flex flex-wrap items-center gap-3">
          {!isNew &&
            shiftType &&
            (shiftType.isActive ? (
              <Zustand art="gut">aktiv</Zustand>
            ) : (
              <span className="font-zahl text-[11px] text-tinte-3">inaktiv</span>
            ))}
          {!isNew && (
            <button onClick={() => loadShiftType()} disabled={loading} className={KNOPF_LEISE}>
              Aktualisieren
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !formData.name.trim()}
            className={KNOPF_PRIMAER}
          >
            {saving ? 'Speichern …' : 'Speichern'}
          </button>
        </span>
      }
    >
      <div className="flex flex-col gap-6">
        <Link to="/einrichten/schichten" className="text-[13px] text-tinte-3 hover:text-tinte-2">
          ← Zurück zu Schichten
        </Link>

        {/* Error */}
        {error && (
          <p className="rounded-[4px] border border-stoer/40 bg-flaeche px-4 py-3 text-[13px] text-stoer">
            {error}
          </p>
        )}

        {/* Inaktiver Konflikt - Reaktivieren anbieten */}
        {inactiveConflict && (
          <Notiz titel="Name schon vergeben">
            <p className="mb-3">
              Ein inaktiver Schichttyp <strong>„{inactiveConflict.name}"</strong> existiert bereits.
            </p>
            <button
              onClick={async () => {
                try {
                  const res = await fetch(`/api/admin/shift-types/${inactiveConflict.id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ isActive: true }),
                  });
                  if (res.ok) {
                    const data = await res.json();
                    navigate(`/einrichten/schichten?focus=types&id=${data.id}`, { replace: true });
                  }
                } catch (err) {
                  console.error('Fehler beim Reaktivieren:', err);
                }
              }}
              className={KNOPF_PRIMAER}
            >
              Schichttyp reaktivieren
            </button>
          </Notiz>
        )}

        {/* Form Sections */}
        <div className="grid gap-5 lg:grid-cols-2">
          {/* Grunddaten */}
          <Karte titel="Grunddaten">
            <div className="flex flex-col gap-4">
              <label className="flex flex-col gap-1.5">
                <span className={MARKE}>
                  Name <span className="text-stoer">*</span>
                </span>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="z.B. Nachtschicht"
                  className={`${EINGABE} w-full`}
                />
              </label>

              <div className="flex flex-col gap-1.5">
                <span className={MARKE}>Farbe im Schichtplan</span>
                <div className="flex flex-wrap items-center gap-2">
                  {FARBVORSCHLAEGE.map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: f })}
                      className={`h-8 w-8 rounded-[4px] border-2 ${formData.color === f ? 'border-tinte' : 'border-linie'}`}
                      style={{ backgroundColor: f }}
                      title={f}
                    />
                  ))}
                  <input
                    type="color"
                    value={formData.color || '#0D6068'}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="h-8 w-10 cursor-pointer rounded-[4px] border border-linie-2 bg-flaeche-2"
                    title="Eigene Farbe wählen"
                  />
                  {formData.color && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, color: '' })}
                      className="text-sm text-tinte-3 underline hover:text-tinte-2"
                    >
                      Farbe entfernen
                    </button>
                  )}
                </div>
                <p className="text-xs text-tinte-3">
                  Färbt die Schicht-Kästchen im Schichtplan. Ohne Farbe gilt die Standard-Optik.
                </p>
              </div>
            </div>
          </Karte>

          {/* Hinweise */}
          <Karte titel="Hinweise">
            <div className="flex flex-col gap-2 text-sm text-tinte-2">
              <p>
                Schichttypen kategorisieren Schichten (z.B. Frühschicht, Spätschicht, Nachtschicht).
              </p>
              <p>
                Schichtregeln referenzieren diesen Typ, um zu definieren, wann welche Schicht aktiv
                ist.
              </p>
            </div>
          </Karte>
        </div>

        {/* Status Section (nur bei Edit) */}
        {!isNew && shiftType && shiftType.isActive && (
          <Karte titel="Schichttyp deaktivieren" marke="wirkt nur auf neue Regeln">
            <div className="flex flex-col gap-3">
              <p className="text-sm text-tinte-2">
                Das Deaktivieren eines Schichttyps entfernt ihn aus der Auswahl für neue Regeln.
                Bestehende Schichtregeln und historische Daten bleiben erhalten.
              </p>
              {/* Zweistufig statt Fenster: die Rückfrage bleibt in derselben Zeile */}
              <div className="flex flex-wrap items-center gap-3">
                {!deleteConfirmOpen ? (
                  <button onClick={() => setDeleteConfirmOpen(true)} className={KNOPF_LEISE}>
                    Schichttyp deaktivieren
                  </button>
                ) : (
                  <>
                    <span className="text-[13px] text-tinte-2">
                      „{shiftType.name}" wird deaktiviert und steht für neue Schichtregeln nicht
                      mehr zur Wahl.
                    </span>
                    <button
                      onClick={handleToggleActive}
                      disabled={deleting}
                      className={KNOPF_PRIMAER}
                    >
                      {deleting ? 'Deaktivieren …' : 'Deaktivieren'}
                    </button>
                    <button onClick={() => setDeleteConfirmOpen(false)} className={KNOPF_LEISE}>
                      Abbrechen
                    </button>
                  </>
                )}
              </div>
            </div>
          </Karte>
        )}

        {/* Reaktivieren Section (nur bei Edit + inaktiv) */}
        {!isNew && shiftType && !shiftType.isActive && (
          <Karte titel="Schichttyp reaktivieren" marke="wieder zur Wahl stellen">
            <div className="flex flex-col gap-3">
              <p className="text-sm text-tinte-2">
                Dieser Schichttyp ist derzeit inaktiv und kann nicht für neue Schichtregeln
                verwendet werden. Durch Reaktivieren wird er wieder in der Auswahl verfügbar.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                {!deleteConfirmOpen ? (
                  <button onClick={() => setDeleteConfirmOpen(true)} className={KNOPF_LEISE}>
                    Schichttyp reaktivieren
                  </button>
                ) : (
                  <>
                    <span className="text-[13px] text-tinte-2">
                      „{shiftType.name}" wird reaktiviert und kann wieder für neue Schichtregeln
                      verwendet werden.
                    </span>
                    <button
                      onClick={handleToggleActive}
                      disabled={deleting}
                      className={KNOPF_PRIMAER}
                    >
                      {deleting ? 'Reaktivieren …' : 'Reaktivieren'}
                    </button>
                    <button onClick={() => setDeleteConfirmOpen(false)} className={KNOPF_LEISE}>
                      Abbrechen
                    </button>
                  </>
                )}
              </div>
            </div>
          </Karte>
        )}
      </div>
    </LeitstandRahmen>
  );
}
